/**
 * Fiche élève du tuteur — « SKILLCARDS » : les cartes de l'élève et leur
 * maîtrise, et « Ajouter une SKILLCARD » (une de SES cartes publiées) que
 * l'élève verra « à travailler » (Feed + notification push). Le tuteur peut
 * retirer une carte qu'il a lui-même ajoutée.
 *
 * Les FICHES DE RÉVISION des packs déjà abordés sont accessibles d'ici, via
 * `?student=` : le prof voit la fiche TELLE QUE L'ÉLÈVE LA VOIT — cartes non
 * débloquées comprises. C'est le seul moyen de savoir ce que l'élève a sous les
 * yeux avant de lui dire de réviser.
 */

import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { stripMath } from '@jeraisonne/db/cards';
import { catalogSections, useCardCatalogForStudent } from '@/lib/cards/card-catalog';
import {
  recentSkillcards,
  skillcardStats,
  useAssignSkillcard,
  useStudentSkillcards,
  useUnassignSkillcard,
} from '@/lib/cards/student-skillcards';
import { SkillcardList } from './skillcard-row';
import { SkillcardPickerField } from './skillcard-picker';
import { RevisionSheetLinks } from './revision-sheet-links';

const inputClass =
  'border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 py-2 text-sm';

export function TutorStudentSkillcards({
  studentId,
  studentName,
  tutorId,
}: {
  studentId: string;
  studentName: string;
  tutorId: string;
}) {
  const skillcards = useStudentSkillcards(studentId);
  const catalog = useCardCatalogForStudent(studentId);
  const assign = useAssignSkillcard();
  const unassign = useUnassignSkillcard();
  const [adding, setAdding] = useState(false);
  const [cardId, setCardId] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const list = skillcards.data ?? [];
  const stats = skillcardStats(list);
  // Cartes publiées de niveau compatible, regroupées par pack (migration 0354).
  const assignedIds = new Set(list.filter((c) => c.assignment).map((c) => c.id));
  const sections = catalogSections(catalog.data)
    .map((sec) => ({ ...sec, items: sec.items.filter((i) => !assignedIds.has(i.id)) }))
    .filter((sec) => sec.items.length > 0);
  const candidates = sections.flatMap((sec) => sec.items);

  const submit = async () => {
    if (!cardId) return;
    setMessage(null);
    try {
      await assign.mutateAsync({ cardId, studentId, tutorId, note });
      setMessage({ kind: 'ok', text: `SKILLCARD ajoutée : ${studentName} est prévenu(e).` });
      setCardId('');
      setNote('');
      setAdding(false);
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof Error ? e.message : 'Ajout impossible.' });
    }
  };

  return (
    <section className="mb-6" aria-labelledby="student-skillcards-title">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2
          id="student-skillcards-title"
          className="font-display text-ink-500 text-[11px] font-bold uppercase tracking-[.1em]"
        >
          SKILLCARDS
        </h2>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-primary-600 hover:bg-primary-50 flex min-h-[2.75rem] items-center gap-1.5 rounded-xl px-2 text-xs font-semibold transition-colors active:scale-95"
          >
            <Plus className="h-4 w-4" aria-hidden /> Ajouter une SKILLCARD
          </button>
        )}
      </div>

      {list.length > 0 && (
        <p className="text-ink-500 mb-3 text-xs">
          {stats.count} carte{stats.count > 1 ? 's' : ''} ·{' '}
          {stats.averageMastery === null
            ? 'pas encore travaillées'
            : `maîtrise moyenne ${stats.averageMastery} %`}{' '}
          · {stats.mastered} maîtrisée{stats.mastered > 1 ? 's' : ''}
          {stats.toWork > 0 && ` · ${stats.toWork} à travailler`}
        </p>
      )}

      <RevisionSheetLinks studentId={studentId} />

      {adding && (
        <div className="border-primary-200 mb-3 space-y-3 rounded-2xl border bg-white p-4">
          {catalog.isLoading ? (
            <p className="text-ink-500 text-sm">Chargement des SKILLCARDS…</p>
          ) : candidates.length === 0 ? (
            <p className="text-ink-500 text-sm">
              Aucune SKILLCARD de son niveau n’est disponible (elles sont peut-être déjà toutes
              ajoutées).
            </p>
          ) : (
            <>
              <SkillcardPickerField
                label="SKILLCARD à travailler"
                items={candidates}
                sections={sections}
                value={cardId || null}
                onChange={(id) => setCardId(id ?? '')}
                title="Choisir une SKILLCARD à travailler"
              />
              <label className="block space-y-1">
                <span className="text-ink-700 text-sm font-semibold">Message (optionnel)</span>
                <input
                  className={inputClass}
                  maxLength={300}
                  placeholder="Revois cette méthode avant le contrôle"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
            </>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              fullWidth
              className="sm:w-auto"
              onClick={() => {
                setAdding(false);
                setCardId('');
                setNote('');
              }}
            >
              Annuler
            </Button>
            <Button
              fullWidth
              className="sm:w-auto"
              disabled={!cardId}
              loading={assign.isPending}
              onClick={() => void submit()}
            >
              Ajouter à {studentName}
            </Button>
          </div>
        </div>
      )}

      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={
            message.kind === 'error' ? 'text-error-600 mb-3 text-sm' : 'text-xp-700 mb-3 text-sm'
          }
        >
          {message.text}
        </p>
      )}

      {skillcards.isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : skillcards.error ? (
        <p role="alert" className="text-accent-600 text-sm">
          Impossible de charger les SKILLCARDS de {studentName}.
        </p>
      ) : (
        <SkillcardList
          cards={recentSkillcards(list, list.length)}
          empty={`${studentName} n’a pas encore de SKILLCARD. Ajoute-lui une carte à travailler.`}
          renderAction={(c) =>
            c.assignment && c.assignment.tutor_id === tutorId ? (
              <button
                type="button"
                aria-label={`Retirer la SKILLCARD ${stripMath(c.title)}`}
                disabled={unassign.isPending}
                onClick={() => {
                  if (
                    !window.confirm(`Retirer « ${stripMath(c.title)} » des cartes à travailler ?`)
                  )
                    return;
                  unassign.mutate({ assignmentId: c.assignment!.id, studentId });
                }}
                className="text-ink-400 hover:text-error-600 flex h-11 w-11 items-center justify-center rounded-xl"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null
          }
        />
      )}
    </section>
  );
}
